import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { Inbox, RefreshCw, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { TestInboxMessageDetail } from '../api/testInbox.api';
import { getTestInboxHtmlUrl, testInboxApi, useTestInboxActions } from '../api/testInbox.api';
import { formatRelativeTime } from '../utils/date';

const CHANNEL_OPTIONS = ['EMAIL', 'SMS', 'WHATSAPP'];
const REFRESH_INTERVAL = 5000;

export function TestInboxPage() {
  const [filterTo, setFilterTo] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TestInboxMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const queryParams: Record<string, unknown> = {};
  if (filterTo) queryParams.to = filterTo;
  if (filterChannel) queryParams.channel = filterChannel;

  const { data: messages, loading, error, refetch } = testInboxApi.withQuery(queryParams).useList();

  const { getMessage, clearAll } = useTestInboxActions();

  const fetchMessages = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchMessages, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMessages]);

  const handleSelectMessage = async (id: string) => {
    if (selectedId === id) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const msg = await getMessage(id);
      setDetail(msg);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClear = async () => {
    try {
      await clearAll();
      setSelectedId(null);
      setDetail(null);
      void refetch();
    } catch {
      // ignore
    }
  };

  const messageList = messages ?? [];

  return (
    <div className="min-h-screen bg-background px-4 py-8 md:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <Inbox className="h-6 w-6 text-primary" /> Test Inbox
            </span>
          }
          description="Internal mailinator — messages to test domains are captured here. Auto-expires after 30 min."
          actions={
            <>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
                Auto-refresh
              </label>
              <Button variant="outline" onClick={fetchMessages} disabled={loading}>
                <RefreshCw className={loading ? 'animate-spin' : undefined} /> Refresh
              </Button>
              <ConfirmAction
                trigger={
                  <Button variant="destructive" disabled={messageList.length === 0}>
                    <Trash2 /> Clear all
                  </Button>
                }
                title="Clear all test inbox messages?"
                description="This deletes every captured message in the test inbox. Cannot be undone."
                confirmLabel="Clear all"
                destructive
                onConfirm={handleClear}
              />
            </>
          }
        />

        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
              <div className="space-y-2">
                <Label htmlFor="ti-to">Recipient</Label>
                <Input
                  id="ti-to"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  placeholder="user@skylabs.test"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ti-channel">Channel</Label>
                <Select
                  value={filterChannel || 'all'}
                  onValueChange={(v) => setFilterChannel(v === 'all' ? '' : v)}
                >
                  <SelectTrigger id="ti-channel">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {CHANNEL_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to fetch messages'}
          </div>
        ) : null}

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>To</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Event</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && messageList.length === 0 ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`s-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={`s-${i}-${j}`}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : messageList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <EmptyState
                      icon={<Inbox className="h-5 w-5" />}
                      title="No messages yet"
                      description="Send a notification to a test address (e.g. anyone@skylabs.test) to see it captured here."
                      className="border-0 bg-transparent"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                messageList.map((msg) => (
                  <TableRow
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg.id)}
                    className={
                      selectedId === msg.id
                        ? 'cursor-pointer bg-accent/10'
                        : 'cursor-pointer hover:bg-muted/50'
                    }
                  >
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatRelativeTime(msg.createdAt)}
                    </TableCell>
                    <TableCell>
                      <ChannelBadge channel={msg.channel as ChannelKind} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{msg.to}</TableCell>
                    <TableCell className="text-xs">
                      {msg.fromName ? `${msg.fromName} <${msg.fromEmail}>` : msg.fromEmail || '—'}
                    </TableCell>
                    <TableCell>{msg.subject || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {msg.eventType || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {messageList.length > 0 ? (
            <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
              {messageList.length} message{messageList.length !== 1 ? 's' : ''} · auto-expires after
              30 min
            </div>
          ) : null}
        </Card>

        {selectedId ? (
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle>{detail?.subject || '(no subject)'}</CardTitle>
                {detail ? (
                  <CardDescription>
                    Received {new Date(detail.createdAt).toLocaleString()}
                  </CardDescription>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedId(null);
                  setDetail(null);
                }}
              >
                <X />
              </Button>
            </CardHeader>
            <CardContent>
              {detailLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : detail ? (
                <div className="space-y-4">
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="font-semibold">To</dt>
                    <dd className="font-mono text-xs">{detail.to}</dd>
                    <dt className="font-semibold">From</dt>
                    <dd>
                      {detail.fromName
                        ? `${detail.fromName} <${detail.fromEmail}>`
                        : detail.fromEmail || '—'}
                    </dd>
                    {detail.replyTo ? (
                      <>
                        <dt className="font-semibold">Reply-To</dt>
                        <dd>{detail.replyTo}</dd>
                      </>
                    ) : null}
                    <dt className="font-semibold">Channel</dt>
                    <dd>
                      <ChannelBadge channel={detail.channel as ChannelKind} />
                    </dd>
                    <dt className="font-semibold">Event</dt>
                    <dd className="font-mono text-xs">{detail.eventType || '—'}</dd>
                    <dt className="font-semibold">Notification ID</dt>
                    <dd className="font-mono text-xs">{detail.notificationId || '—'}</dd>
                  </dl>

                  {detail.bodyHtml ? (
                    <div className="space-y-2">
                      <Label>HTML body</Label>
                      <iframe
                        src={getTestInboxHtmlUrl(detail.id)}
                        title="Email body"
                        sandbox=""
                        className="h-[420px] w-full rounded-md border border-border bg-white"
                      />
                    </div>
                  ) : detail.bodyText ? (
                    <div className="space-y-2">
                      <Label>Text body</Label>
                      <pre className="overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-4 text-sm">
                        {detail.bodyText}
                      </pre>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No body content.</p>
                  )}

                  {detail.metadata && Object.keys(detail.metadata).length > 0 ? (
                    <div className="space-y-2">
                      <Label>Metadata</Label>
                      <pre className="max-h-52 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs">
                        {JSON.stringify(detail.metadata, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-destructive">Message not found or expired.</p>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
